import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import { Icon, X } from "./icon";

export type BadgeVariant = "neutral" | "accent" | "success" | "warning" | "danger";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** White outlined-chip style (filter chips). */
  outline?: boolean;
  icon?: ReactNode;
  /** Renders a dismiss affordance (filter chips). */
  onDismiss?: () => void;
}

export function Badge({ variant = "neutral", outline = false, icon, onDismiss, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cx("pz-badge", variant !== "neutral" && `pz-badge--${variant}`, outline && "pz-badge--outline", className)}
      {...rest}
    >
      {icon}
      {children}
      {onDismiss && (
        <button type="button" className="pz-badge__dismiss" aria-label="Remove" onClick={onDismiss}>
          <Icon icon={X} size="sm" />
        </button>
      )}
    </span>
  );
}

export type AccuracyLevel = "high" | "medium" | "low";

const ACCURACY_VARIANT: Record<AccuracyLevel, BadgeVariant> = {
  high: "success",
  medium: "warning",
  low: "danger",
};

/** Geocode-accuracy chip (M3) — semantic subtle-wash roles per DESIGN_SYSTEM.md §11. */
export function AccuracyChip({ level, children, ...rest }: Omit<BadgeProps, "variant"> & { level: AccuracyLevel }) {
  return (
    <Badge variant={ACCURACY_VARIANT[level]} {...rest}>
      {children ?? level}
    </Badge>
  );
}
